import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { withFollow } from "./followHelpers";

export const listCashRegistersSkill: Skill = {
  id: "listCashRegisters",
  description:
    "Lista TODAS las cajas registradoras del restaurante (incluye archivadas). Para saber cuáles tienen sesión abierta usa listCashRegistersWithOpenSession.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" } },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    return withFollow(ctx, {
      intro: "Listando cajas…",
      successMsg: "✓ Cajas obtenidas",
      run: async () => {
        const { data } = await apiWithAuth.get(
          `/restaurants/${restaurantId}/cash-registers`,
          ctx.accessToken,
        );
        const items: any[] = data?.items ?? data?.data ?? data ?? [];
        return {
          ok: true as const,
          result: {
            count: items.length,
            registers: items.map((r) => ({
              id: r.id,
              name: r.name,
              description: r.description,
              isActive: r.isActive,
              isArchived: r.isArchived,
            })),
          },
        };
      },
    });
  },
};

export const createCashRegisterSkill: Skill = {
  id: "createCashRegister",
  description: "Crea una nueva caja registradora física en el restaurante.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      name: { type: "string" },
      description: { type: "string" },
    },
    required: ["restaurantId", "name"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const name = String(args.name ?? "").trim();
    if (!restaurantId || !name) return { ok: false, error: "Faltan datos." };
    const description = args.description ? String(args.description) : undefined;

    const ok = await ctx.confirmation.confirm({
      title: "Crear caja registradora",
      details: [
        { label: "Nombre", value: name },
        ...(description ? [{ label: "Descripción", value: description }] : []),
      ],
      confirmLabel: "Crear",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: `Creando caja "${name}"…`,
      successMsg: `✓ Caja "${name}" creada`,
      restaurantId,
      targetTab: "cajas",
      targetTabLabel: "Cajas",
      run: async () => {
        const body: Record<string, unknown> = { name };
        if (description) body.description = description;
        const { data } = await apiWithAuth.post(
          `/restaurants/${restaurantId}/cash-registers`,
          body,
          ctx.accessToken,
        );
        return {
          ok: true as const,
          result: { id: data?.id, name },
          humanSummary: `Caja "${name}" creada.`,
        };
      },
    });
  },
};

export const updateCashRegisterSkill: Skill = {
  id: "updateCashRegister",
  description: "Actualiza nombre o descripción de una caja registradora.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      cashRegisterId: { type: "string" },
      name: { type: "string" },
      description: { type: "string" },
    },
    required: ["restaurantId", "cashRegisterId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const cashRegisterId = String(args.cashRegisterId ?? "");
    if (!restaurantId || !cashRegisterId) return { ok: false, error: "Faltan IDs." };
    const patch: Record<string, unknown> = {};
    const details: Array<{ label: string; value: string }> = [];
    if (args.name) { patch.name = String(args.name); details.push({ label: "Nombre", value: patch.name as string }); }
    if (args.description !== undefined) { patch.description = String(args.description); details.push({ label: "Descripción", value: patch.description as string }); }
    if (Object.keys(patch).length === 0) return { ok: false, error: "Nada que actualizar." };

    const ok = await ctx.confirmation.confirm({ title: "Actualizar caja", details, confirmLabel: "Actualizar" });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Actualizando caja…",
      successMsg: "✓ Caja actualizada",
      restaurantId,
      targetTab: "cajas",
      targetTabLabel: "Cajas",
      run: async () => {
        await apiWithAuth.patch(
          `/restaurants/${restaurantId}/cash-registers/${cashRegisterId}`,
          patch,
          ctx.accessToken,
        );
        return { ok: true as const, result: { id: cashRegisterId, ...patch }, humanSummary: "Caja actualizada." };
      },
    });
  },
};

export const archiveCashRegisterSkill: Skill = {
  id: "archiveCashRegister",
  description:
    "Archiva una caja registradora (la oculta sin eliminarla). NOTA: el sistema NO permite eliminar cajas permanentemente para preservar el historial de sesiones — usa archive en lugar de delete.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" }, cashRegisterId: { type: "string" } },
    required: ["restaurantId", "cashRegisterId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const cashRegisterId = String(args.cashRegisterId ?? "");
    if (!restaurantId || !cashRegisterId) return { ok: false, error: "Faltan IDs." };

    const ok = await ctx.confirmation.confirm({
      title: "Archivar caja registradora",
      description: "Se ocultará. Podrás restaurarla después.",
      confirmLabel: "Archivar",
      danger: true,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Archivando caja…",
      successMsg: "✓ Caja archivada",
      restaurantId,
      targetTab: "cajas",
      targetTabLabel: "Cajas",
      run: async () => {
        await apiWithAuth.patch(
          `/restaurants/${restaurantId}/cash-registers/${cashRegisterId}/archive`,
          {},
          ctx.accessToken,
        );
        return { ok: true as const, result: { archived: true }, humanSummary: "Caja archivada." };
      },
    });
  },
};

export const restoreCashRegisterSkill: Skill = {
  id: "restoreCashRegister",
  description: "Restaura una caja registradora archivada.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" }, cashRegisterId: { type: "string" } },
    required: ["restaurantId", "cashRegisterId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const cashRegisterId = String(args.cashRegisterId ?? "");
    if (!restaurantId || !cashRegisterId) return { ok: false, error: "Faltan IDs." };
    return withFollow(ctx, {
      intro: "Restaurando caja…",
      successMsg: "✓ Caja restaurada",
      restaurantId,
      targetTab: "cajas",
      targetTabLabel: "Cajas",
      run: async () => {
        await apiWithAuth.patch(
          `/restaurants/${restaurantId}/cash-registers/${cashRegisterId}/restore`,
          {},
          ctx.accessToken,
        );
        return { ok: true as const, result: { restored: true }, humanSummary: "Caja restaurada." };
      },
    });
  },
};

export const toggleCashRegisterActiveSkill: Skill = {
  id: "toggleCashRegisterActive",
  description: "Activa o desactiva una caja registradora (sin archivarla).",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" }, cashRegisterId: { type: "string" } },
    required: ["restaurantId", "cashRegisterId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const cashRegisterId = String(args.cashRegisterId ?? "");
    if (!restaurantId || !cashRegisterId) return { ok: false, error: "Faltan IDs." };

    const ok = await ctx.confirmation.confirm({
      title: "Cambiar estado de caja",
      description: "Vas a alternar el estado activo/inactivo de la caja.",
      confirmLabel: "Cambiar",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Cambiando estado…",
      successMsg: "✓ Estado actualizado",
      restaurantId,
      targetTab: "cajas",
      targetTabLabel: "Cajas",
      run: async () => {
        const { data } = await apiWithAuth.patch(
          `/restaurants/${restaurantId}/cash-registers/${cashRegisterId}/toggle-active`,
          {},
          ctx.accessToken,
        );
        return {
          ok: true as const,
          result: { isActive: data?.isActive },
          humanSummary: `Caja ${data?.isActive ? "activada" : "desactivada"}.`,
        };
      },
    });
  },
};
